/**
 * Утилиты для расчетов при пополнении баланса
 * Чистые функции без зависимостей от Vue компонентов
 */

/**
 * Рассчитывает финальную сумму пополнения
 * @param {Array} selectedCampaigns - массив выбранных кампаний с суммами
 * @param {Number} accountAmount - сумма для аккаунта
 * @returns {Number}
 */
export function calculateFinalAmount(selectedCampaigns, accountAmount) {
  let amount = 0;

  if (selectedCampaigns && selectedCampaigns.length) {
    selectedCampaigns.forEach((el) => {
      amount += Number(el.amount) || 0;
    });
  }

  if (accountAmount) {
    amount += Number(accountAmount);
  }

  return amount;
}

/**
 * Рассчитывает сумму с учетом комиссии (RU версия)
 * @param {Number} finalAmount - финальная сумма
 * @param {Number} commission - процент комиссии
 * @returns {Number}
 */
export function calculateAmountWithCommission(finalAmount, commission) {
  if (!commission) {
    return finalAmount;
  }

  return (finalAmount * (100 - commission)) / 100;
}

/**
 * Рассчитывает НДС от суммы после комиссии
 * @param {Number} amountWithCommission - сумма после комиссии
 * @returns {Number}
 */
export function calculateNds(amountWithCommission, vat = 22) {
  const nds = (amountWithCommission / (100 + vat)) * vat;
  return Math.ceil(nds);
}

/**
 * Рассчитывает базовую сумму пополнения (без рекламного сбора)
 * @param {Number} finalAmount - финальная сумма (может включать рекламный сбор)
 * @param {Number} advtTax - процент рекламного сбора
 * @param {Boolean} includesAdvtTax - флаг: включает ли сумма рекламный сбор
 * @param {String} service - название сервиса
 * @returns {Number} базовая сумма без рекламного сбора
 */
export function calculateBaseDepositAmount(finalAmount, advtTax, includesAdvtTax, service) {
  if (service === 'google' || !advtTax || !finalAmount) {
    return finalAmount;
  }

  if (includesAdvtTax) {
    // Если сумма включает рекламный сбор, вычисляем базовую сумму
    // Базовая сумма = итоговая / (1 + advt_tax/100)
    return finalAmount / (1 + advtTax / 100);
  } else {
    // Если рекламный сбор добавляется сверху, базовая сумма = введенной сумме
    return finalAmount;
  }
}

/**
 * Рассчитывает сумму рекламного сбора
 * @param {Number} finalAmount - финальная сумма
 * @param {Number} baseDepositAmount - базовая сумма без рекламного сбора
 * @param {Number} advtTax - процент рекламного сбора
 * @param {Boolean} includesAdvtTax - флаг: включает ли сумма рекламный сбор
 * @param {String} service - название сервиса
 * @returns {Number}
 */
export function calculateAdvtTaxAmount(
  finalAmount,
  baseDepositAmount,
  advtTax,
  includesAdvtTax,
  service
) {
  if (service === 'google' || !advtTax || !finalAmount) {
    return 0;
  }

  if (includesAdvtTax) {
    // Если сумма включает рекламный сбор, вычисляем его из итоговой суммы
    // Рекламный сбор = итоговая - базовая (базовая уже округлена)
    return finalAmount - Math.round(baseDepositAmount);
  } else {
    // Рекламный сбор считается от ИСХОДНОЙ суммы и округляется по общему правилу
    return Math.round((finalAmount * advtTax) / 100);
  }
}

/**
 * Рассчитывает итоговую сумму с учетом всех налогов и сборов (RU версия)
 * @param {Number} finalAmountWithCommission - сумма после комиссии
 * @param {Number} nds - НДС
 * @returns {Number}
 */
export function calculateTotalSum(finalAmountWithCommission, nds) {
  return finalAmountWithCommission - nds;
}

/**
 * Находит процент комиссии для суммы пополнения
 * @param {Number} finalAmount - финальная сумма
 * @param {Object} commissionConditions - условия комиссии
 * @param {String} service - название сервиса
 * @param {Number} accountCommission - комиссия аккаунта (если есть)
 * @returns {Number|null}
 */
export function findCommissionPercent(
  finalAmount,
  commissionConditions,
  service,
  accountCommission = null
) {
  // Если у аккаунта есть своя комиссия для пополнения, используем её
  if (accountCommission !== null && accountCommission !== undefined) {
    return accountCommission;
  }

  if (!commissionConditions || !service || !commissionConditions[service]) {
    return null;
  }

  const conditions = commissionConditions[service];

  // Если условия не массив, возвращаем значение
  if (!Array.isArray(conditions)) {
    return Number(conditions) || null;
  }

  if (!conditions.length) {
    return null;
  }

  // Сначала ищем точное совпадение (с max)
  for (let condition of conditions) {
    if (condition.max !== null && condition.max !== undefined) {
      if (finalAmount >= Number(condition.min) && finalAmount <= Number(condition.max)) {
        return condition.percent;
      }
    }
  }

  // Затем ищем диапазон без max (для больших сумм)
  for (let i = conditions.length - 1; i >= 0; i--) {
    const condition = conditions[i];
    if (condition.max === null || condition.max === undefined) {
      if (finalAmount >= Number(condition.min)) {
        return condition.percent;
      }
    }
  }

  return null;
}

/**
 * Рассчитывает курс валюты
 * @param {Array} currencyRates - массив курсов валют
 * @param {String} serviceCurrency - валюта сервиса
 * @param {String} localCurrency - локальная валюта
 * @returns {Number}
 */
export function getCurrencyRate(currencyRates, serviceCurrency, localCurrency) {
  if (!currencyRates || !serviceCurrency || serviceCurrency === localCurrency) {
    return 1;
  }

  const rate = currencyRates.find((el) => el.to === serviceCurrency && el.from === localCurrency);

  return rate ? rate.value : 1;
}

/**
 * Рассчитывает специальный курс для PLN
 * @param {Array} currencyRates - массив курсов валют
 * @param {String} localCurrency - локальная валюта
 * @param {Number} coursePercent - процент наценки на курс
 * @returns {Number}
 */
export function getPlnRate(currencyRates, localCurrency, coursePercent = 0) {
  const serviceCurrency = 'PLN';

  if (localCurrency === serviceCurrency) {
    return 1;
  }

  const rate = currencyRates.find((el) => el.to === serviceCurrency && el.from === localCurrency);

  if (!rate) {
    return 1;
  }

  if (coursePercent) {
    return rate.value / (1 + coursePercent / 100);
  }

  return rate.value;
}

/**
 * Рассчитывает итоговую сумму с учетом валютного курса (RU версия)
 * @param {Number} totalSum - итоговая сумма
 * @param {Number} currencyRate - курс валюты
 * @returns {Number}
 */
export function calculateTotalWithRate(totalSum, currencyRate) {
  if (!totalSum || !currencyRate) {
    return 0;
  }

  return totalSum * currencyRate;
}

/**
 * Получает локальную валюту (RU версия всегда RUB)
 * @returns {String}
 */
export function getLocalCurrency() {
  return 'RUB';
}

/**
 * Основная функция для расчета всех сумм при пополнении (RU версия)
 * @param {Object} params - параметры расчета
 * @param {Array} params.selectedCampaigns - выбранные кампании
 * @param {Number} params.accountAmount - сумма для аккаунта
 * @param {Object} params.selectedAccount - выбранный аккаунт
 * @param {Object} params.activeAccount - активный аккаунт
 * @param {Array} params.currencyRates - курсы валют
 * @param {Boolean} params.includesAdvtTax - флаг: включает ли сумма рекламный сбор
 * @returns {Object} объект с рассчитанными суммами
 */
export function calculateDepositTotals(params) {
  const {
    selectedCampaigns = [],
    accountAmount = 0,
    selectedAccount,
    activeAccount,
    currencyRates = [],
    vat = 22,
    includesAdvtTax = false,
  } = params;

  if (!selectedAccount || !activeAccount) {
    return {
      finalAmount: 0,
      baseDepositAmount: 0,
      baseDepositAmountExact: 0,
      commissionPercent: null,
      amountWithCommission: 0,
      nds: 0,
      advtTaxAmount: 0,
      totalSum: 0,
      currencyRate: 1,
      totalWithRate: 0,
      finalAmountWithAdvtTax: 0,
      totalWithAdvtTax: 0,
      localCurrency: 'RUB',
      serviceCurrency: null,
    };
  }

  // Базовые расчеты
  const finalAmount = calculateFinalAmount(selectedCampaigns, accountAmount);
  const localCurrency = getLocalCurrency();
  const serviceCurrency = selectedAccount.service_currency;

  // Базовая сумма пополнения (без рекламного сбора)
  const baseDepositAmountExact = calculateBaseDepositAmount(
    finalAmount,
    activeAccount.advt_tax,
    includesAdvtTax,
    selectedAccount.service
  );
  // Округляем по общему правилу (0.5 вверх)
  const baseDepositAmount = Math.round(baseDepositAmountExact);

  // Комиссия считается от базовой суммы (без рекламного сбора)
  const commissionPercent = findCommissionPercent(
    baseDepositAmount,
    activeAccount.commissions_conditions,
    selectedAccount.service,
    selectedAccount.commission_replenishment
  );

  const amountWithCommission = calculateAmountWithCommission(
    baseDepositAmount,
    commissionPercent || 0
  );

  // НДС
  const nds =
    activeAccount.tax_conditions?.[selectedAccount.service] === true
      ? calculateNds(amountWithCommission, vat)
      : 0;

  // Рекламный сбор
  const advtTaxAmount = calculateAdvtTaxAmount(
    finalAmount,
    baseDepositAmountExact,
    activeAccount.advt_tax,
    includesAdvtTax,
    selectedAccount.service
  );

  // Итоговая сумма после комиссии (для отображения, когда НДС не применяется)
  let finalAmountWithAdvtTax;
  if (includesAdvtTax) {
    // Если сумма включает рекламный сбор, используем сумму после комиссии от базовой суммы
    finalAmountWithAdvtTax = amountWithCommission;
  } else {
    // Если рекламный сбор добавляется сверху, сумма после комиссии + рекламный сбор
    finalAmountWithAdvtTax = amountWithCommission + advtTaxAmount;
  }

  // Общая сумма списания с клиента (включая рекламный сбор)
  const totalWithAdvtTax = includesAdvtTax
    ? finalAmount // Если сумма включает рекламный сбор, итоговая сумма списания = введенной сумме
    : finalAmount + advtTaxAmount; // Если рекламный сбор добавляется сверху

  // Итоговая сумма зачисления
  let totalSum;
  if (activeAccount.tax_conditions?.[selectedAccount.service] === true) {
    // Если НДС применяется, общая сумма зачисления = базовая сумма - НДС
    // (рекламный сбор не идет на баланс, он удерживается)
    totalSum = amountWithCommission - nds;
  } else {
    // Если НДС не применяется, общая сумма зачисления = базовая сумма после комиссии
    totalSum = amountWithCommission;
  }

  // Курс валюты
  const currencyRate = getCurrencyRate(currencyRates, serviceCurrency, localCurrency);

  // Итоговая сумма с курсом (RU логика)
  let totalWithRate = 0;
  if (activeAccount.tax_conditions?.[selectedAccount.service] === true) {
    totalWithRate = totalSum * currencyRate;
  } else {
    totalWithRate = finalAmountWithAdvtTax * currencyRate;
  }

  return {
    finalAmount,
    baseDepositAmount,
    baseDepositAmountExact,
    commissionPercent,
    amountWithCommission,
    nds,
    advtTaxAmount,
    totalSum,
    currencyRate,
    totalWithRate,
    finalAmountWithAdvtTax,
    totalWithAdvtTax,
    localCurrency,
    serviceCurrency,
  };
}
